import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  itemName: string;
  isLoading?: boolean;
  requiresConfirmation?: boolean;
  dangerKeyword?: string;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  itemName,
  isLoading = false,
  requiresConfirmation = true,
  dangerKeyword = 'DELETE',
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [errors, setErrors] = useState<string>('');

  const isConfirmationValid = !requiresConfirmation || 
    confirmationText.trim().toUpperCase() === dangerKeyword.toUpperCase();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (requiresConfirmation && !isConfirmationValid) {
      setErrors(`Silmek için "${dangerKeyword}" yazınız`);
      return;
    }

    onConfirm();
  };

  const handleClose = () => {
    setConfirmationText('');
    setErrors('');
    onClose();
  };

  const handleConfirmationChange = (value: string) => {
    setConfirmationText(value);
    if (errors) {
      setErrors('');
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 dark:text-white flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400 mr-3" />
                    <span>{title}</span>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </Dialog.Title>

                <form onSubmit={handleSubmit}>
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {message}
                    </p>
                    
                    <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                        <strong>Silinecek öğe:</strong> {itemName}
                      </p>
                      {requiresConfirmation && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                          Bu işlem geri alınamaz ve tüm ilgili veriler kalıcı olarak silinecektir.
                        </p>
                      )}
                    </div>

                    {requiresConfirmation && (
                      <div className="mt-4">
                        <label htmlFor="confirmation" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Silme işlemini onaylamak için "<strong>{dangerKeyword}</strong>" yazın:
                        </label>
                        <input
                          type="text"
                          id="confirmation"
                          value={confirmationText}
                          onChange={(e) => handleConfirmationChange(e.target.value)}
                          className={`mt-2 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-2 sm:text-sm dark:bg-gray-700 dark:text-white ${
                            errors
                              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                              : 'border-gray-300 dark:border-gray-600 focus:border-red-500 focus:ring-red-500'
                          }`}
                          placeholder={dangerKeyword}
                          autoComplete="off"
                          disabled={isLoading}
                        />
                        {errors && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors}</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={isLoading}
                      className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:focus:ring-offset-gray-800"
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading || (requiresConfirmation && !isConfirmationValid)}
                      className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:focus:ring-offset-gray-800"
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Siliniyor...
                        </>
                      ) : (
                        'Sil'
                      )}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DeleteConfirmationModal; 